import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  grantClubAdmin,
  inviteClubAdmin,
  listClubAdminInvites,
  listSuperAdminClubAdminCandidates,
  listSuperAdminClubAdmins,
  resendClubAdminInvite,
  revokeClubAdminInvite,
  revokeSuperAdminClubAdmin,
} from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { TkticSelect } from "@/components/ui/TkticSelect";
import type { AdminClubRow, Club } from "@/lib/types";
import { PlatformQueryNotice } from "./PlatformQueryNotice";

function formatErr(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}

const FIELD =
  "min-w-0 flex-1 rounded-control border border-rule bg-field px-2 py-1.5 text-xs text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const LABEL =
  "text-[0.6rem] font-display uppercase tracking-wider text-ink-muted";

/**
 * Operator-side club-admin management for ONE club: seated admins with a
 * demote action (the server's last-admin guard 409s — surfaced as copy),
 * a promote-existing-member picker, invite-by-email, and the pending-invite
 * list with resend/revoke. The super-admin mirror of the club-side
 * `ClubAdminsCard` (role-model 2b), hitting `/api/super-admin/clubs/{id}/…`.
 *
 * Rendered inside a dialog on the Clubs list today; designed to re-host
 * inline on the club detail page without changes.
 */
export function ClubAdminsPanel({ club }: { club: Club | AdminClubRow }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const adminsKey = unscopedKey("super-admin-club-admins", { clubId: club.id });
  const candidatesKey = unscopedKey("super-admin-club-admin-candidates", {
    clubId: club.id,
  });
  const invitesKey = unscopedKey("club-admin-invites", { clubId: club.id });

  const adminsQuery = useQuery({
    queryKey: adminsKey,
    queryFn: () => listSuperAdminClubAdmins(club.id),
  });
  const candidatesQuery = useQuery({
    queryKey: candidatesKey,
    queryFn: () => listSuperAdminClubAdminCandidates(club.id),
  });
  const invitesQuery = useQuery({
    queryKey: invitesKey,
    queryFn: () => listClubAdminInvites(club.id),
  });

  const [email, setEmail] = useState("");
  const [promoteId, setPromoteId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [demoteTarget, setDemoteTarget] = useState<{
    user_id: string;
    label: string;
  } | null>(null);

  const admins = adminsQuery.data?.admins ?? [];
  const candidates = candidatesQuery.data?.candidates ?? [];
  const pending = (invitesQuery.data ?? []).filter(
    (i) => i.status === "pending",
  );
  const isLastAdmin = admins.length <= 1;

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: adminsKey }),
      queryClient.invalidateQueries({ queryKey: candidatesKey }),
      queryClient.invalidateQueries({ queryKey: invitesKey }),
      // Admin counts on the clubs list change with grants/demotions.
      queryClient.invalidateQueries({
        queryKey: unscopedKey("super-admin-clubs"),
      }),
      // ...and on the club detail page's Usage card when hosted there
      // (no-op when those queries aren't mounted). The Usage card reads
      // member/admin counts from a separate usage query, so it needs its
      // own key invalidated — a detail-key invalidation doesn't reach it.
      queryClient.invalidateQueries({
        queryKey: unscopedKey("super-admin-club-detail", { clubId: club.id }),
      }),
      queryClient.invalidateQueries({
        queryKey: unscopedKey("super-admin-club-usage", { clubId: club.id }),
      }),
    ]);

  const demoteMut = useMutation({
    mutationFn: (userId: string) => revokeSuperAdminClubAdmin(club.id, userId),
    onSuccess: async () => {
      setDemoteTarget(null);
      toast.success("Club admin demoted");
      await invalidate();
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError && err.status === 409
          ? "Can't remove the only club admin. Add another admin first."
          : formatErr(err);
      setDemoteTarget(null);
      toast.error("Couldn't update club admins", { description: msg });
    },
  });

  const promoteMut = useMutation({
    mutationFn: (userId: string) => grantClubAdmin(club.id, userId),
    onSuccess: async () => {
      setPromoteId("");
      setError(null);
      toast.success("Member promoted to club admin");
      await invalidate();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't promote member", { description: formatErr(err) });
    },
  });

  const inviteMut = useMutation({
    mutationFn: () => inviteClubAdmin(club.id, email.trim()),
    onSuccess: async () => {
      setEmail("");
      setError(null);
      toast.success("Club-admin invite sent");
      await invalidate();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't send invite", { description: formatErr(err) });
    },
  });

  const resendMut = useMutation({
    mutationFn: (inviteId: string) => resendClubAdminInvite(club.id, inviteId),
    onSuccess: () => {
      toast.success("Invite re-sent with a fresh link");
      void queryClient.invalidateQueries({ queryKey: invitesKey });
    },
    onError: (err) =>
      toast.error("Couldn't resend invite", { description: formatErr(err) }),
  });
  const revokeInviteMut = useMutation({
    mutationFn: (inviteId: string) => revokeClubAdminInvite(club.id, inviteId),
    onSuccess: () => {
      toast.success("Invite revoked");
      void queryClient.invalidateQueries({ queryKey: invitesKey });
    },
    onError: (err) =>
      toast.error("Couldn't revoke invite", { description: formatErr(err) }),
  });

  return (
    <div
      data-testid="super-admin-club-admins-panel"
      className="flex flex-col gap-3"
    >
      {adminsQuery.isLoading ? (
        <p className="font-mono text-[0.65rem] text-ink-muted">
          Loading admins…
        </p>
      ) : adminsQuery.error ? (
        <p className="font-mono text-xs text-destructive-text">
          Failed to load club admins.
        </p>
      ) : admins.length === 0 ? (
        <p className="rounded-control border border-rule bg-field px-2 py-2 text-[0.65rem] font-mono text-ink-muted">
          No admins yet — send the first invite below.
        </p>
      ) : (
        <ul
          data-testid="super-admin-club-admins-list"
          className="flex flex-col gap-1"
        >
          {admins.map((admin) => (
            <li
              key={admin.user_id}
              data-testid="super-admin-club-admins-row"
              className="flex items-center justify-between gap-3 rounded-control border border-rule bg-elevated px-2 py-1.5"
            >
              <div className="min-w-0">
                <p className="truncate text-xs text-ink">
                  {admin.display_name || admin.username}
                </p>
                <p className="truncate font-mono text-[0.6rem] text-ink-muted">
                  @{admin.username}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={isLastAdmin || demoteMut.isPending}
                title={
                  isLastAdmin
                    ? "A club must keep at least one admin"
                    : undefined
                }
                onClick={() =>
                  setDemoteTarget({
                    user_id: admin.user_id,
                    label: admin.display_name || admin.username,
                  })
                }
                data-testid="super-admin-club-admins-demote"
              >
                Demote
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="platform-admin-tasks">
        <PlatformQueryNotice
          pending={candidatesQuery.isPending}
          error={candidatesQuery.error}
          label="members eligible for promotion"
          retry={candidatesQuery.refetch}
        />
        {candidates.length > 0 ? (
          <form
            className="platform-admin-task flex flex-col gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setError(null);
              if (!promoteId) {
                setError("Pick a member to promote.");
                return;
              }
              promoteMut.mutate(promoteId);
            }}
          >
            <label className="flex flex-col gap-1 text-xs">
              <span className={LABEL}>Promote an existing member</span>
              <div className="flex gap-2">
                <TkticSelect
                  value={promoteId}
                  onChange={setPromoteId}
                  options={candidates.map((c) => ({
                    value: c.user_id,
                    label: `${c.display_name || c.username} (@${c.username})`,
                  }))}
                  placeholder="Pick a member…"
                  ariaLabel="Member to promote to club admin"
                  size="sm"
                  data-testid="super-admin-club-admins-promote-select"
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={promoteMut.isPending}
                  data-testid="super-admin-club-admins-promote-submit"
                >
                  {promoteMut.isPending ? "Promoting…" : "Promote"}
                </Button>
              </div>
            </label>
          </form>
        ) : null}

        <form
          className="platform-admin-task flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (!email.trim()) {
              setError("Enter an email to invite.");
              return;
            }
            inviteMut.mutate();
          }}
        >
          <label className="flex flex-col gap-1 text-xs">
            <span className={LABEL}>Invite a new club admin</span>
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@club.example"
                data-testid="super-admin-invite-email"
                className={FIELD}
              />
              <Button
                type="submit"
                size="sm"
                disabled={inviteMut.isPending}
                data-testid="super-admin-invite-submit"
              >
                {inviteMut.isPending ? "Sending…" : "Send invite"}
              </Button>
            </div>
          </label>
          {error ? (
            <p className="font-mono text-[0.65rem] text-destructive-text">
              {error}
            </p>
          ) : null}
        </form>
      </div>
      <PlatformQueryNotice
        pending={invitesQuery.isPending}
        error={invitesQuery.error}
        label="club admin invitations"
        retry={invitesQuery.refetch}
      />
      {pending.length > 0 ? (
        <div
          className="flex flex-col gap-1"
          data-testid="super-admin-pending-invites"
        >
          <span className={LABEL}>Pending invites</span>
          <ul className="flex flex-col gap-1">
            {pending.map((invite) => (
              <li
                key={invite.id}
                className="flex items-center justify-between gap-2 rounded-control border border-rule bg-field px-2 py-1"
              >
                <span className="truncate font-mono text-xs text-ink">
                  {invite.email}
                </span>
                <span className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => resendMut.mutate(invite.id)}
                    disabled={resendMut.isPending || revokeInviteMut.isPending}
                    data-testid="super-admin-invite-resend"
                    className="rounded px-1.5 py-0.5 text-[0.65rem] font-display uppercase tracking-wider text-ink-muted transition-colors hover:bg-surface hover:text-ink disabled:opacity-50"
                  >
                    Resend
                  </button>
                  <button
                    type="button"
                    onClick={() => revokeInviteMut.mutate(invite.id)}
                    disabled={resendMut.isPending || revokeInviteMut.isPending}
                    data-testid="super-admin-invite-revoke"
                    className="rounded px-1.5 py-0.5 text-[0.65rem] font-display uppercase tracking-wider text-destructive-text transition-colors hover:bg-surface disabled:opacity-50"
                  >
                    Revoke
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {demoteTarget ? (
        <ConfirmDialog
          title="Demote club admin"
          message={
            <p className="text-xs text-ink-muted">
              Remove club-admin rights from{" "}
              <strong className="text-ink">{demoteTarget.label}</strong> in{" "}
              {club.name}? They stay a member of the club and can be re-promoted
              later.
            </p>
          }
          confirmLabel={demoteMut.isPending ? "Removing…" : "Demote"}
          confirmDisabled={demoteMut.isPending}
          danger
          onConfirm={() => demoteMut.mutate(demoteTarget.user_id)}
          onClose={() => setDemoteTarget(null)}
          data-testid="super-admin-club-admins-demote-confirm"
        />
      ) : null}
    </div>
  );
}
